import { NavLink, Link } from 'react-router'
import {
  LayoutDashboard, Users, ArrowRightLeft, Building2, FileText,
  BookOpen, Mail, Inbox, BarChart3, GraduationCap, DollarSign,
  Settings, Phone, Search, Calendar, CalendarClock, MapPin, CheckSquare, ClipboardCheck,
  ChevronDown, Share2, MessageSquare, DoorOpen, Mic, Wrench,
  Calculator, PanelLeftClose, PanelLeftOpen, ListChecks, LayoutTemplate,
  // Group faces for the collapsed rail. Deliberately NOT any icon a nav item
  // already uses — see the note on NavGroup.icon.
  Contact, Briefcase, Home, Lightbulb, type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { notificationDb } from '@/lib/notificationDb'
import { uniqueTopic } from '@/lib/realtime'
import { loadSidebarCollapsed, saveSidebarCollapsed } from '@/lib/sidebarCollapse'
import { readBrandLogoUrl } from '@/lib/brandTheme'
import { NotificationBell } from './NotificationBell'
import { OrgSwitcher } from './OrgSwitcher'
import { AskRizeButton } from './AskRizePanel'

type NavItem = { to: string; icon: LucideIcon; label: string }
// `icon` is the group's face in the collapsed rail, where there is no room for
// the word. It is not decoration — without it the rail's four section headers
// are four identical chevrons.
//
// ⚠️ IT MUST NOT BE AN ICON ONE OF ITS OWN ITEMS USES. The first pass gave each
// group its first item's icon and the rail rendered Users directly above Users
// (Clients over Contacts), one of them highlighted as the current page — seen in
// the browser, and unreadable. Each group gets a face of its own.
type NavGroup = { label: string; icon: LucideIcon; items: NavItem[] }

// Dashboard stands alone at the top (outside any group) — the logo links here
// too. Settings stands alone at the bottom. Everything else lives inside a
// collapsible group.
const dashboardItem: NavItem = { to: '/', icon: LayoutDashboard, label: 'Dashboard' }

const navGroups: NavGroup[] = [
  {
    label: 'Clients',
    icon: Contact,
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts' },
      { to: '/calls', icon: Phone, label: 'Calls' },
      // Beside Calls, because it is the same thing from a different device — a conversation
      // with a client, transcribed. It is in Clients rather than Deals for that reason.
      { to: '/recordings', icon: Mic, label: 'Recordings' },
      { to: '/inbox', icon: Inbox, label: 'Inbox' },
      { to: '/texting', icon: MessageSquare, label: 'Texting' },
      { to: '/follow-up', icon: Mail, label: 'Campaigns' },
      // RIZ047: beside Campaigns because they are the other half of the same job — a
      // campaign is a drip a contact is enrolled in; a template is one message an agent
      // picks and sends now. Same entry in MobileBottomNav — keep the two together.
      { to: '/templates', icon: LayoutTemplate, label: 'Templates' },
      { to: '/referrals', icon: Share2, label: 'Referrals' },
      // Next to Referrals because they are the same kind of thing from the brokerage's
      // point of view — an outside party you send business to. Referrals are other
      // AGENTS and private to whoever added them; vendors are trades and shared by the
      // whole brokerage (mig 270).
      { to: '/vendors', icon: Wrench, label: 'Vendors' },
    ],
  },
  {
    label: 'Deals',
    icon: Briefcase,
    items: [
      { to: '/transactions', icon: ArrowRightLeft, label: 'Transactions' },
      // RIZ051 (Dakri, 2026-09-09): the plans were reachable only from a button inside
      // Transactions, and "I had to ask AI where to do that". Listed right under the
      // board they serve. Same entry in MobileBottomNav — keep the two together.
      { to: '/transactions/plans', icon: ListChecks, label: 'Action Plans' },
      { to: '/deadlines', icon: CalendarClock, label: 'Deadlines' },
      { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
      { to: '/calendar', icon: Calendar, label: 'Calendar' },
      { to: '/showings', icon: DoorOpen, label: 'Showings' },
      { to: '/commissions', icon: DollarSign, label: 'Commissions' },
    ],
  },
  {
    label: 'Properties',
    icon: Home,
    items: [
      { to: '/properties', icon: Building2, label: 'Listings' },
      // Row 61: the CMA was reachable only from a button inside Listings. It is a distinct
      // job — valuing a property — not a way of browsing them, so it gets its own entry.
      { to: '/cma', icon: Calculator, label: 'CMA' },
      { to: '/fsbo', icon: MapPin, label: 'FSBO Monitor' },
      { to: '/ccr-library', icon: BookOpen, label: 'CC&R Library' },
      { to: '/documents', icon: FileText, label: 'Documents' },
      // Forms (/forms) is deliberately NOT listed. Hidden 2026-09-09 on John's call while
      // tracker row 71 — whether the brokerage may distribute the forms without a Certificate
      // of Authenticity — sits with HELLA's attorney. The route and the FormsLibrary page still
      // exist; when the answer comes back, restore the entry here AND in MobileBottomNav.
    ],
  },
  {
    label: 'Insights',
    icon: Lightbulb,
    items: [
      { to: '/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/review', icon: ClipboardCheck, label: 'Review' },
      { to: '/training', icon: GraduationCap, label: 'Training' },
    ],
  },
]

const bottomItems: NavItem[] = [{ to: '/settings', icon: Settings, label: 'Settings' }]

// Persist which groups the user has collapsed so the layout survives reloads.
//
// ⚠️ THIS SET IS SHARED BETWEEN BOTH WIDTHS ON PURPOSE (row 67). The icon rail
// renders each group header as its icon and toggles this same set, so opening
// Deals in the rail leaves it open when the sidebar is widened again. Two
// separate sets would mean the sidebar rearranged itself every time you
// changed its width — and the rail's compactness is exactly the group
// mechanism doing its job, not a second layout.
const COLLAPSE_KEY = 'rize:sidebar:collapsed-groups'

// Every group starts COLLAPSED by default. Once the user toggles anything we
// persist and honor their explicit set (an empty stored array = all expanded).
const allGroupsCollapsed = () => new Set(navGroups.map((g) => g.label))

function loadCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY)
    if (!raw) return allGroupsCollapsed()
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed)
      ? new Set(parsed.filter((x): x is string => typeof x === 'string'))
      : allGroupsCollapsed()
  } catch {
    return allGroupsCollapsed()
  }
}

// No `onNavigate` prop. It existed for a mobile drawer that renders this
// component and closes on navigation — nothing ever did. AppLayout is the only
// caller and mounts it `hidden md:block`; the mobile surface is
// MobileBottomNav, whose "More" sheet is self-contained and closes itself
// (`setMoreOpen(false)`). Removed 2026-09-03 with the row-67 rail rather than
// carried as a parameter no caller passes. If a drawer ever lands, add it back
// with the caller in the same change.
export function Sidebar() {
  const { user, activeOrg, profile } = useAuth()
  // Primitive ids for the effect/realtime deps: activeOrg/user are fresh OBJECTS
  // on every SIGNED_IN-on-focus re-emit + membership refetch, so keying the
  // channel effect on them would resubscribe on every tab focus. The string ids
  // are stable — the useTextingRealtime/useTodayTextCount rule (#249).
  const orgId = activeOrg?.organization_id
  const userId = user?.id
  const [pendingDrafts, setPendingDrafts] = useState(0)
  const [pendingTasks, setPendingTasks] = useState(0)
  const [pendingReview, setPendingReview] = useState(0)
  const [unreadTexts, setUnreadTexts] = useState(0)
  const [unfiledRecordings, setUnfiledRecordings] = useState(0)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsedGroups)
  // Row 67 — the whole sidebar collapses to a 64px icon rail. Read once from
  // storage on mount (lib/sidebarCollapse owns the try/catch, because a browser
  // that refuses site data must not take the navigation down with it).
  const [collapsed, setCollapsed] = useState<boolean>(loadSidebarCollapsed)
  // Row 41 — the brokerage's logo in place of the wordmark. Keyed by url so a
  // broken upload falls back for exactly that url and a replacement is tried.
  const logoUrl = readBrandLogoUrl(activeOrg?.organizations?.brand)
  const orgName = activeOrg?.organizations?.name ?? 'Rize OS'
  const [brokenLogo, setBrokenLogo] = useState<string | null>(null)
  const showLogo = !!logoUrl && brokenLogo !== logoUrl

  // `[` toggles it, dispatched by useKeyboardShortcuts through the same
  // `rize:shortcut` custom event the help dialog and the "new" shortcut use —
  // so the key binding stays in one place and this component owns the state.
  useEffect(() => {
    function onShortcut(e: Event) {
      if ((e as CustomEvent).detail !== 'toggle-sidebar') return
      setCollapsed((prev) => { saveSidebarCollapsed(!prev); return !prev })
    }
    window.addEventListener('rize:shortcut', onShortcut)
    return () => window.removeEventListener('rize:shortcut', onShortcut)
  }, [])

  useEffect(() => {
    if (!orgId || !userId) return
    // Narrowed locals: the guard doesn't propagate into the nested loadCounts.
    const oid = orgId
    const uid = userId
    async function loadCounts() {
      const [draftsR, tasksR, reviewR, textsR, recordingsR] = await Promise.all([
        // YOUR drafts (mig 259) — the badge must agree with the Drafts tab it
        // points at, and that tab is now the caller's own mail. An org-wide
        // count beside a per-user list is a badge that never reaches zero.
        supabase.from('email_messages').select('id', { count: 'exact', head: true })
          .eq('agent_id', uid).eq('organization_id', oid).eq('status', 'draft').eq('ai_drafted', true),
        // YOUR open tasks (RIZ140): the link opens Tasks on "mine", and an office-wide
        // count beside a per-user list is a badge that never reaches zero.
        supabase.from('tasks').select('id', { count: 'exact', head: true })
          .eq('organization_id', oid).eq('assigned_to', uid).in('status', ['pending', 'in_progress']),
        // pending + apply_failed = what /review actually lists (a failed apply
        // still needs a human retry-or-reject decision).
        supabase.from('proposed_actions').select('id', { count: 'exact', head: true })
          .eq('organization_id', oid).in('status', ['pending', 'apply_failed']),
        // Unread texting = MY unread inbound-text bell rows for THIS org — the
        // webhook deep-links them to /texting/<contactId>, and that link is the
        // producer/consumer contract (src/lib/texting.ts). Org-scoped to match
        // the sibling badges; opening the conversation clears the rows.
        // in_app (mig 295): this badge must count exactly what the bell shows.
        // The flag is stamped at WRITE time, so switching text alerts off does
        // not retire bells that already exist — it stops new ones appearing in
        // either place, and this filter is what keeps the two agreeing.
        notificationDb.from('notifications').select('id', { count: 'exact', head: true })
          .eq('organization_id', oid).eq('user_id', uid).eq('in_app', true)
          .is('read_at', null).like('link', '/texting/%'),
        // Recordings waiting to be filed. ORG-SCOPED ONLY, exactly like the siblings and
        // exactly like useRecordings. RLS is the privacy boundary (mig 466, RIZ172): an
        // unattached recording counts only for the person who recorded it, and there is
        // no admin arm. Do not add an owner filter here. The badge must equal what the
        // page shows, including an attached recording the contact's agent did not record.
        // The unassigned count itself is unattached, so the database already limits it
        // to the recorder.
        supabase.from('plaud_recordings').select('id', { count: 'exact', head: true })
          .eq('organization_id', oid).eq('status', 'unassigned'),
      ])
      setPendingDrafts(draftsR.count ?? 0)
      setPendingTasks(tasksR.count ?? 0)
      setPendingReview(reviewR.count ?? 0)
      setUnreadTexts(textsR.count ?? 0)
      setUnfiledRecordings(recordingsR.count ?? 0)
    }
    loadCounts()
    const interval = setInterval(loadCounts, 30000)
    // The texting badge should feel live (it's a messaging surface): also
    // refresh the moment THIS user's notifications change — mark-read on opening
    // a conversation, or a new inbound-text bell — instead of waiting up to 30s
    // for the poll. Sibling badges ride along. Keyed on the primitive ids so a
    // tab-focus SIGNED_IN re-emit doesn't resubscribe the channel (the
    // useTextingRealtime rule, #249).
    const channel = supabase
      .channel(uniqueTopic('sidebar-badges'))
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${uid}`,
      }, () => { void loadCounts() })
      // Recordings arrive UNPROMPTED — a pocket recorder syncing in the background is the one
      // badge here that no action in this tab produces, so a 30s poll is the difference between
      // "it appeared" and "it appeared eventually". Same table + filter as useRecordings' own
      // subscription (mig 238 put it in the publication for this), and it only ever triggers a
      // refetch — the payload is never read, because what THIS viewer may count is RLS's answer,
      // not the replication stream's.
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'plaud_recordings',
        filter: `organization_id=eq.${oid}`,
      }, () => { void loadCounts() })
      .subscribe()
    return () => {
      clearInterval(interval)
      supabase.removeChannel(channel)
    }
  }, [orgId, userId])

  const badges: Record<string, number> = { '/follow-up': pendingDrafts, '/tasks': pendingTasks, '/review': pendingReview, '/texting': unreadTexts, '/recordings': unfiledRecordings }

  function toggleGroup(label: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])) } catch { /* storage unavailable — keep in-memory */ }
      return next
    })
  }

  function toggleCollapsed() {
    setCollapsed((prev) => { saveSidebarCollapsed(!prev); return !prev })
  }

  function renderLink(item: NavItem) {
    const badgeCount = badges[item.to] ?? 0
    // In the rail the label is gone, so the accessible name has to come from
    // aria-label on EVERY item, not only the badged ones — an icon-only link
    // with no name is invisible to a screen reader and unfindable by
    // getByRole. `title` gives the same words to a sighted mouse user.
    const named = badgeCount > 0 ? item.label + ', ' + badgeCount + ' pending' : item.label
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        aria-label={collapsed || badgeCount > 0 ? named : undefined}
        title={collapsed ? named : undefined}
        className={({ isActive }) =>
          cn(
            'relative flex items-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
            collapsed ? 'h-10 w-10 justify-center self-center' : 'gap-3 px-3 py-1.5',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
          )
        }
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {!collapsed && <span>{item.label}</span>}
        {badgeCount > 0 && (
          <span
            aria-hidden="true"
            className={cn(
              'rounded-full bg-primary text-[10px] font-bold text-primary-foreground',
              collapsed
                ? 'absolute -right-1 -top-1 min-w-[16px] px-1 text-center leading-4'
                : 'ml-auto px-1.5 py-0.5',
            )}
          >
            {badgeCount}
          </span>
        )}
      </NavLink>
    )
  }

  // One instance, rendered in one of two places — beside the bell when there is
  // a row to sit in, on its own line in the rail where there is not.
  return (
    <aside
      id="app-sidebar"
      aria-label="Main navigation"
      className={cn(
        'flex h-[100dvh] flex-col overflow-y-auto border-r border-sidebar-border bg-sidebar',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* ⚠️ ONE TREE FOR BOTH WIDTHS, not a collapsed branch and an expanded one.
          The rail is 64px and holds exactly one control per line, so these three
          stack under the wordmark instead of sitting beside it — but rendering
          that as a second JSX branch would REMOUNT them on every toggle, and
          NotificationBell owns a realtime subscription, an unread fetch and the
          open/closed state of its panel. Same elements, different classes: only
          the layout changes, and the toggle uses `order-first` to lead the
          column while staying last in the DOM (and last in the tab order beside
          the wordmark) when the sidebar is wide. */}
      <div className={cn(
        'flex shrink-0',
        collapsed ? 'flex-col items-center px-2 pt-2' : 'h-16 items-center justify-between px-6',
      )}>
        <Link
          to="/"
          aria-label={(showLogo ? orgName : 'Rize OS') + ' — go to Dashboard'}
          className="flex min-w-0 items-center rounded text-xl font-bold tracking-[-0.04em] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {/* Row 41, the logo half: the brokerage's own mark where the app's
              wordmark was, on both widths. An image that fails to load falls
              back to the wordmark for THAT url — never a broken-image glyph at
              the top of every page — and a new url gets its own chance. */}
          {showLogo ? (
            <img
              src={logoUrl}
              alt={orgName}
              onError={() => setBrokenLogo(logoUrl)}
              className={collapsed ? 'h-8 w-10 object-contain' : 'h-8 max-w-[150px] object-contain'}
            />
          ) : (
            <>{collapsed ? 'r' : 'rize'}<span className="text-primary" aria-hidden="true">.</span></>
          )}
        </Link>
        <div className={cn('flex', collapsed ? 'flex-col items-center' : 'items-center gap-1')}>
          <AskRizeButton />
          <NotificationBell />
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={(collapsed ? 'Expand sidebar' : 'Collapse sidebar') + ' ( [ )'}
            className={cn(
              'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              collapsed && 'order-first',
            )}
          >
            {collapsed
              ? <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              : <PanelLeftClose className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
      </div>

      <div className={cn('shrink-0 pb-2', collapsed ? 'flex justify-center' : 'px-3')}>
        <button
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
          aria-label="Open search (Cmd K)"
          title={collapsed ? 'Search (Cmd K)' : undefined}
          className={cn(
            'flex items-center rounded-md border border-border text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            collapsed ? 'h-10 w-10 justify-center' : 'w-full gap-2 px-3 py-1.5 text-xs',
          )}
        >
          <Search className={collapsed ? 'h-4 w-4' : 'h-3 w-3'} aria-hidden="true" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Search...</span>
              <kbd className="rounded border border-border px-1 py-0.5 text-[9px]" aria-hidden="true">Cmd K</kbd>
            </>
          )}
        </button>
      </div>

      <nav aria-label="Primary" className={cn('flex flex-col gap-1 py-2', collapsed ? 'px-2' : 'px-3')}>
        {/* Dashboard — standalone, top, outside any group */}
        {renderLink(dashboardItem)}

        {navGroups.map((group) => {
          const isCollapsed = collapsedGroups.has(group.label)
          const groupPending = group.items.reduce((sum, it) => sum + (badges[it.to] ?? 0), 0)
          const sectionId = 'nav-group-' + group.label.toLowerCase()
          // The rail has no room for the word, so the group's own icon carries
          // it — four identical chevrons stacked in a 64px column name nothing.
          // aria-expanded/aria-controls are unchanged, so the section is still
          // announced as the disclosure it has always been.
          const named = groupPending > 0 ? group.label + ', ' + groupPending + ' pending' : group.label
          return (
            // A hairline above each group in the rail. With labels gone it is
            // the only thing that says "a new section starts here" rather than
            // "one more destination"; the full sidebar has the uppercase word
            // for that and needs no rule.
            <div
              key={group.label}
              className={cn('flex flex-col', collapsed && 'mt-1 border-t border-sidebar-border pt-1')}
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                aria-expanded={!isCollapsed}
                aria-controls={sectionId}
                aria-label={collapsed || (isCollapsed && groupPending > 0) ? named : undefined}
                title={collapsed ? named : undefined}
                className={cn(
                  'mt-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                  collapsed
                    ? 'relative flex h-9 w-10 items-center justify-center self-center'
                    : 'flex items-center gap-2 px-3 py-1.5',
                  collapsed && !isCollapsed ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {collapsed ? (
                  <group.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                  <ChevronDown
                    className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')}
                    aria-hidden="true"
                  />
                )}
                {!collapsed && <span className="flex-1 text-left">{group.label}</span>}
                {isCollapsed && groupPending > 0 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'rounded-full bg-primary text-[10px] font-bold text-primary-foreground',
                      collapsed
                        ? 'absolute -right-1 -top-1 min-w-[16px] px-1 text-center leading-4'
                        : 'px-1.5 py-0.5',
                    )}
                  >
                    {groupPending}
                  </span>
                )}
              </button>
              <div
                id={sectionId}
                hidden={isCollapsed}
                className={cn('mt-0.5 flex flex-col gap-0.5', collapsed && 'items-center')}
              >
                {group.items.map(renderLink)}
              </div>
            </div>
          )
        })}
      </nav>

      <div className="flex-1" aria-hidden="true" />

      {/* Settings goes through renderLink like every other destination — it used
          to carry its own copy of the same markup, which is how the rail would
          have shipped with one link still wearing its label. */}
      <div className={cn('flex shrink-0 flex-col py-2', collapsed ? 'items-center px-2' : 'px-3')}>
        {bottomItems.map(renderLink)}
      </div>

      {/* Footer identity chip is DISPLAY ONLY (operator decision 2026-07-08 —
          clicking the picture/email must do nothing; profile editing lives in
          Settings > Preferences). The org switcher stays interactive.
          In the rail there is no room for either line of text, so the avatar
          carries both as its tooltip and switching orgs means widening the
          sidebar — the one thing the rail defers rather than keeps. */}
      <div className={cn('shrink-0 border-t border-sidebar-border', collapsed ? 'flex justify-center p-2' : 'p-3')}>
        {collapsed ? (
          <div
            title={[activeOrg?.organizations?.name, user?.email].filter(Boolean).join(' — ')}
            className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-primary/20 text-xs font-medium text-primary"
          >
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
              : (user?.email?.[0]?.toUpperCase() ?? '?')}
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <div
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/20 text-xs font-medium text-primary"
            >
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                : (user?.email?.[0]?.toUpperCase() ?? '?')}
            </div>
            {/* min-w-0 (not truncate) — overflow:hidden here would clip the switcher's dropdown */}
            <div className="min-w-0 flex-1">
              <OrgSwitcher />
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
